import React, { useState } from 'react'

import { WidgetAdminControlsComponent } from 'features/views/LayoutEditor/types'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'

import useDebounce from 'v2/ui/utils/useDebounce'

import { Box } from 'ui/components/Box'
import { Field } from 'ui/components/Field'
import { RadioCard, RadioCardGroup } from 'ui/components/Radio'
import { Select, SelectOption } from 'ui/components/Select'
import { Textarea } from 'ui/components/Textarea'

import {
    CustomCodeFieldSourceRef,
    CustomCodeFileSourceRef,
    CustomCodeSourceRef,
    CustomCodeWidgetType,
} from './types'
import { getSupportedCustomCodeSourceFields } from './utils'

const DEBOUNCE_RATE = 300 // ms

type CustomCodeWidgetAdminControlsProps = {}

export const CustomCodeWidgetAdminControls: WidgetAdminControlsComponent<
    CustomCodeWidgetType,
    CustomCodeWidgetAdminControlsProps
> = ({ widget, onChange }) => {
    const { source } = widget.attrs

    const { fields } = useLayoutEditorContext()
    const sourceFields = getSupportedCustomCodeSourceFields(fields)

    const sourceType = source.type

    const fieldApiName = (source as CustomCodeFieldSourceRef)?.fieldApiName ?? ''
    const field = fields.find((f) => f.api_name === fieldApiName) ?? undefined

    const debouncedOnChange = useDebounce(onChange, DEBOUNCE_RATE) as typeof onChange

    const text = (source as CustomCodeFileSourceRef)?.text ?? ''
    const [localText, setLocalText] = useState(text)
    const onChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value

        setLocalText(newValue)
        debouncedOnChange((attrs) => {
            attrs.set('source', {
                type: 'inline',
                text: newValue,
            })
        })
    }

    return (
        <Box flex column px="l" gap="3xl" height="full" overflowY="auto">
            <Field label="Data source">
                <RadioCardGroup
                    value={sourceType}
                    onValueChange={(value: string) => {
                        let newValue: CustomCodeSourceRef
                        if (value === 'field') {
                            newValue = {
                                type: 'field',
                                fieldApiName: '',
                            }
                        } else {
                            newValue = {
                                type: 'inline',
                                text: '',
                            }
                        }

                        onChange((attrs) => {
                            attrs.set('source', newValue)
                        })
                    }}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                    }}
                >
                    <RadioCard value="field" icon={{ name: 'Pencil' }}>
                        From field
                    </RadioCard>
                    <RadioCard value="inline" icon={{ name: 'Text' }}>
                        Inline code
                    </RadioCard>
                </RadioCardGroup>
            </Field>
            {sourceType === 'field' && (
                <Select
                    placeholder="Select field..."
                    label="Field"
                    isSearchable
                    value={field?.api_name ?? ''}
                    onChange={(value) => {
                        onChange((attrs) => {
                            attrs.set('source', {
                                type: 'field',
                                fieldApiName: value,
                            })
                        })
                    }}
                >
                    {sourceFields.map((field) => (
                        <SelectOption
                            key={field.api_name}
                            value={field.api_name}
                            label={field.label}
                        />
                    ))}
                </Select>
            )}
            {sourceType === 'inline' && (
                <Textarea
                    label="Value"
                    name="text"
                    size="m"
                    autoFocus
                    resizable
                    value={localText}
                    onChange={onChangeText}
                />
            )}
        </Box>
    )
}
